import React, { useEffect, useState } from "react";
import {
  TextField,
  Button,
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useAuth } from "./authContext"; // Assuming you have the authContext setup
import axios from "axios";
import { Accordion, AccordionDetails, AccordionSummary } from "@mui/joy";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import EditIcon from "@mui/icons-material/Edit";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import { useProject } from "./ProjectContext";
import config from "./config";
import { apiCall } from "./api";

const CommentsSection = ({ submission_id, setLeaveCommentOpen = false }) => {
  const { user, globalRoles, projectRoles } = useAuth(); // Access user info and admin status from authContext
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [commentText, setCommentText] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [isUIDEditable, setIsUIDEditable] = useState(false);
  const [isSubmission_idEditable, setIsSubmission_idEditable] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [currentComment, setCurrentComment] = useState(null); // The comment being edited
  const [submitCompleted, setSubmitCompleted] = useState(false);
  const [editFirstName, setEditFirstName] = useState("");
  const [editLastName, setEditLastName] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [editCommentText, setEditCommentText] = useState("");
  const [editUID, setEditUID] = useState("");
  const [editSubmission_id, setEditSubmission_id] = useState("");
  const { project } = useProject();

  // State for input values

  const [editableSubmission_id, setEditableSubmission_id] =
    useState(submission_id);
  // Handlers for enabling edit mode
  const handleUIDClick = () => setIsUIDEditable(!isUIDEditable);
  const handleSubmission_idClick = () =>
    setIsSubmission_idEditable(!isSubmission_idEditable);

  const [commentsExpanded, setCommentsExpanded] = useState(false);
  const [formExpanded, setFormExpanded] = useState(false);

  const handleCommentsToggle = () => {
    setCommentsExpanded(!commentsExpanded);
  };

  const handleFormToggle = () => {
    setFormExpanded(!formExpanded);
  };

  useEffect(() => {
    if (setLeaveCommentOpen) {
      setFormExpanded(true);
    }
    setCommentsExpanded(comments.length >= 1);
  }, [comments, setLeaveCommentOpen]);

  const determineUID = () => {
    if (user) {
      return user.uid; // Use Firebase UID if user is logged in
    } else {
      return `${lastName}_${email}`; // Use firstName + lastName if not logged in
    }
  };
  const [uid, setUid] = useState(determineUID);

  useEffect(() => {
    setLoading(true);
    if (!submission_id) {
      console.error("Submission ID is undefined.");
      return;
    }
    const fetchComments = async () => {
      try {
        const formData = new FormData();
        formData.append("action", "getAll");
        formData.append("submission_id", submission_id);

        const response = await axios.post(
          `${config.getApiUrl("manage_comments.php")}`,
          formData,
        );
        setComments(response.data);
      } catch (error) {
        console.error("Error fetching comments:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchComments();
  }, [submission_id, submitCompleted]);

  // Auto-populate name and email if user is logged in
  useEffect(() => {
    if (user) {
      setFirstName(user.displayName?.split(" ")[0] || "");
      setLastName(user.displayName?.split(" ")[1] || "");
      setEmail(user.email || "");
    }
  }, [user]);

  // Handle comment submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    // Auth refactor 2026-05-27: dropped formData.append("uid", ...). The
    // server now writes the comment uid from the verified Firebase ID
    // token (apiCall attaches it). This also means anonymous comments
    // are no longer accepted by this path — the UI should already gate
    // the submit button on a logged-in user, but if it doesn't, the
    // server will 401 and the catch below fires.
    const formData = new FormData();
    formData.append("action", "create");
    formData.append("submission_id", editableSubmission_id);
    formData.append("first_name", firstName);
    formData.append("last_name", lastName);
    formData.append("email", email);
    formData.append("comment", commentText);

    try {
      const response = await apiCall("manage_comments.php", {
        method: "POST",
        body: formData,
      });

      if (response?.message === "Comment created successfully") {
        setCommentText("");
        alert("Comment added successfully!");
        setSubmitCompleted(!submitCompleted);
      } else {
        console.error("Error adding comment:", response);
        alert("Failed to add comment. Please check the console for details.");
      }
    } catch (error) {
      console.error("Error submitting comment:", error);
      alert("Error submitting comment. Please check the console for details.");
    }
  };
  const handleDelete = async (commentId) => {
    try {
      const formData = new FormData();
      formData.append("action", "delete");
      formData.append("id", commentId);

      const response = await axios.post(
        `${config.getApiUrl("manage_comments.php")}`,
        formData,
      );
      if (response.data.message === "Comment deleted successfully") {
        setComments(comments.filter((comment) => comment.id !== commentId)); // Remove deleted comment from state
      }
    } catch (error) {
      console.error("Error deleting comment:", error);
    }
  };

  // Function to handle editing a comment (placeholder for actual edit functionality)
  const handleEditClick = (comment) => {
    setCurrentComment(comment);
    setEditFirstName(comment.first_name);
    setEditLastName(comment.last_name);
    setEditEmail(comment.email);
    setEditCommentText(comment.comment);
    setOpenEditDialog(true);
    setEditUID(comment.uid);
    setEditSubmission_id(comment.submission_id);
  };

  // Function to handle submitting the edited comment
  const handleEditSubmit = async () => {
    try {
      const formData = new FormData();
      formData.append("action", "edit");
      formData.append("id", currentComment.id); // Send the ID of the comment being edited
      formData.append("first_name", editFirstName);
      formData.append("last_name", editLastName);
      formData.append("email", editEmail);
      formData.append("comment", editCommentText);
      formData.append("uid", editUID);
      formData.append("submission_id", editSubmission_id);

      // Log form data before submitting
      console.log("Submitting edit with data:", {
        id: currentComment.id,
        first_name: editFirstName,
        last_name: editLastName,
        email: editEmail,
        comment: editCommentText,
        uid: currentComment.uid,
        submission_id: currentComment.submission_id,
      });

      const response = await axios.post(
        `${config.getApiUrl("manage_comments.php")}`,
        formData,
      );
      console.log("Response from server:", response.data); // Log the server response

      if (response.data.message === "Comment updated successfully") {
        // Update the comment in the state
        setComments(
          comments.map((comment) =>
            comment.id === currentComment.id
              ? {
                  ...comment,
                  first_name: editFirstName,
                  last_name: editLastName,
                  email: editEmail,
                  comment: editCommentText,
                }
              : comment,
          ),
        );
        setOpenEditDialog(false); // Close the dialog
      } else {
        console.error("Error updating comment:", response.data);
      }
    } catch (error) {
      console.error("Error submitting edited comment:", error); // Log any errors
    }
  };

  const adminOrMod = () => {
    const projectAdminRole = project.projectid + "_Admin"; // Construct the specific project admin role

    return (
      globalRoles.isAdmin ||
      (projectRoles &&
        projectRoles.length > 0 &&
        projectRoles.includes(projectAdminRole))
    );
  };
  return (
    <>
      <Box>
        <Accordion expanded={commentsExpanded} onChange={handleCommentsToggle}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              "& .MuiAccordionSummary-indicator": {
                display: "none",
              },
              "& .MuiAccordionSummary-button:not(.Mui-selected, [aria-selected='true']):hover":
                {
                  backgroundColor: "transparent",
                  color: "var(--theme-color)",
                },
            }}
          >
            <Typography
              variant="h6"
              sx={{
                flexGrow: 1,
                color: "var(--theme-color)",
                fontSize: "0.8rem",
                fontWeight: 700,
                textTransform: "capitalize",
              }}
            >
              {loading
                ? "Loading comments..."
                : `Comments (${comments?.length || 0})`}
            </Typography>
            {comments?.length > 0 && (
              <Button
                onClick={handleCommentsToggle}
                sx={{
                  color: "var(--theme-color)",
                  fontSize: "0.8rem",
                  textTransform: "capitalize",
                  textDecoration: "underline",
                  p: 0,
                  "&:hover": {
                    background: "transparent",
                  },
                }}
              >
                {commentsExpanded ? "Close" : "Open"}
              </Button>
            )}
          </AccordionSummary>
          <AccordionDetails>
            {/* Comments List */}
            {loading ? (
              <Typography>Loading comments...</Typography>
            ) : (
              <List>
                {comments.map((comment) => (
                  <ListItem
                    key={comment.id}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "unset",
                    }}
                  >
                    <Box flexGrow={1}>
                      <ListItemText
                        primary={
                          <span
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <span
                              style={{
                                color: "var(--theme-color)",
                                fontSize: "0.8rem",
                                fontWeight: 700,
                                textTransform: "capitalize",
                              }}
                            >
                              {comment.first_name} {comment.last_name}
                            </span>
                            <span
                              style={{
                                color: "#938983",
                                fontWeight: 400,
                                fontSize: "0.8rem",
                              }}
                            >
                              {`${new Date(
                                comment.created_at,
                              ).toLocaleDateString("en-US", {
                                month: "long",
                                day: "numeric",
                                year: "numeric",
                              })} ${new Date(comment.created_at)
                                .toLocaleTimeString("en-US", {
                                  hour: "numeric",
                                  minute: "2-digit",
                                  hour12: true,
                                })
                                .replace(" AM", "am")
                                .replace(" PM", "pm")}`}
                            </span>
                          </span>
                        }
                        secondary={`${comment.comment} `}
                        sx={{
                          "& .MuiListItemText-primary": {
                            color: "var(--theme-color)",
                            fontWeight: 700,
                            textTransform: "capitalize",
                          },
                          "& .MuiListItemText-secondary": {
                            color: "var(--theme-color)",
                          },
                        }}
                      />
                    </Box>

                    {/* Conditionally render Edit/Delete buttons if the user is the author or an admin */}
                    {(user?.uid === comment.uid || adminOrMod()) && (
                      <Box>
                        <IconButton
                          onClick={() => handleEditClick(comment)}
                          aria-label="edit"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          onClick={() => handleDelete(comment.id)}
                          aria-label="delete"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                  </ListItem>
                ))}
              </List>
            )}
          </AccordionDetails>
        </Accordion>

        <br />

        {/* Comment Form */}
        <Accordion expanded={formExpanded} onChange={handleFormToggle}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="leave-comment-content"
            id="leave-comment-header"
            sx={{
              "& .MuiAccordionSummary-indicator": {
                display: "none",
              },
              "& .MuiAccordionSummary-button": {
                textAlign: "end",
              },
              "& .MuiAccordionSummary-button:not(.Mui-selected, [aria-selected='true']):hover":
                {
                  backgroundColor: "transparent",
                  color: "var(--theme-color)",
                },
            }}
          >
            <Typography
              variant="h6"
              sx={{
                flexGrow: 1,
                color: "var(--theme-color)",
                fontSize: "0.8rem",
                fontWeight: 700,
                textTransform: "inherit",
              }}
            >
              Leave a Comment
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Paper elevation={3} sx={{ p: 2 }}>
              <form onSubmit={handleSubmit}>
                {/* Only show First Name, Last Name, Email fields if no user is logged in */}
                {!user && (
                  <>
                    <TextField
                      label="First Name"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      fullWidth
                      margin="normal"
                       sx={{                      
                      "& .MuiInputLabel-root": { color: "var(--theme-color)" },
                      "& .MuiInputLabel-root.Mui-focused": {
                        color: "var(--theme-color)",
                      },
                      "& .MuiOutlinedInput-root": {                       
                        "&.Mui-focused fieldset": {
                          borderColor: "var(--theme-color)",
                        },
                      },
                    }}
                    />
                    <TextField
                      label="Last Name"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      fullWidth
                      margin="normal"
                       sx={{                      
                      "& .MuiInputLabel-root": { color: "var(--theme-color)" },
                      "& .MuiInputLabel-root.Mui-focused": {
                        color: "var(--theme-color)",
                      },
                      "& .MuiOutlinedInput-root": {                       
                        "&.Mui-focused fieldset": {
                          borderColor: "var(--theme-color)",
                        },
                      },
                    }}
                    />
                    <TextField
                      label="Email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      fullWidth
                      margin="normal"
                       sx={{                      
                      "& .MuiInputLabel-root": { color: "var(--theme-color)" },
                      "& .MuiInputLabel-root.Mui-focused": {
                        color: "var(--theme-color)",
                      },
                      "& .MuiOutlinedInput-root": {                       
                        "&.Mui-focused fieldset": {
                          borderColor: "var(--theme-color)",
                        },
                      },
                    }}
                    />
                  </>
                )}

                {/* If user is logged in but not an admin, auto-fill but hide these fields */}
                {user && !globalRoles.isAdmin && (
                  <>
                    <input type="hidden" value={firstName} />
                    <input type="hidden" value={lastName} />
                    <input type="hidden" value={email} />
                  </>
                )}

                {/* If the user is an admin, show the fields but auto-populate them */}
                {globalRoles.isAdmin && (
                  <>
                    <TextField
                      label="First Name"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      fullWidth
                      margin="normal"
                      InputProps={{
                        readOnly: false, // This field is editable by default
                      }}
                      sx={{
                        "& .MuiInputLabel-root": {
                          color: "var(--theme-color)",
                        },
                        "& .MuiInputLabel-root.Mui-focused": {
                          color: "var(--theme-color)",
                        },
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            borderColor: "var(--theme-color)",
                          },
                          "&:hover fieldset": {
                            borderColor: "var(--theme-color)",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "var(--theme-color)",
                          },
                        },
                      }}
                    />
                    <TextField
                      label="Last Name"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      fullWidth
                      margin="normal"
                      InputProps={{
                        readOnly: false, // This field is editable by default
                      }}
                      sx={{
                        "& .MuiInputLabel-root": {
                          color: "var(--theme-color)",
                        },
                        "& .MuiInputLabel-root.Mui-focused": {
                          color: "var(--theme-color)",
                        },
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            borderColor: "var(--theme-color)",
                          },
                          "&:hover fieldset": {
                            borderColor: "var(--theme-color)",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "var(--theme-color)",
                          },
                        },
                      }}
                    />
                    <TextField
                      label="Email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      fullWidth
                      margin="normal"
                      InputProps={{
                        readOnly: false, // This field is editable by default
                      }}
                      sx={{
                        "& .MuiInputLabel-root": {
                          color: "var(--theme-color)",
                        },
                        "& .MuiInputLabel-root.Mui-focused": {
                          color: "var(--theme-color)",
                        },
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            borderColor: "var(--theme-color)",
                          },
                          "&:hover fieldset": {
                            borderColor: "var(--theme-color)",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "var(--theme-color)",
                          },
                        },
                      }}
                    />

                    {/* UID Field - Greyed out by default, editable when clicking the pencil icon */}
                    <Box display="flex" alignItems="center">
                      <TextField
                        label="UID"
                        value={uid}
                        onChange={(e) => setUid(e.target.value)}
                        fullWidth
                        margin="normal"
                        InputProps={{
                          readOnly: !isUIDEditable, // Greyed out unless toggled editable
                        }}
                        sx={{
                          input: {
                            color: isUIDEditable ? "black" : "grey", // Change text color when editable
                          },
                        }}
                      />
                      <IconButton onClick={handleUIDClick}>
                        <EditIcon />
                      </IconButton>
                    </Box>

                    <Box display="flex" alignItems="center">
                      <TextField
                        label="submission_id"
                        value={editableSubmission_id}
                        onChange={(e) =>
                          setEditableSubmission_id(e.target.value)
                        }
                        fullWidth
                        margin="normal"
                        InputProps={{
                          readOnly: !isSubmission_idEditable, // Greyed out unless toggled editable
                        }}
                        sx={{
                          input: {
                            color: isSubmission_idEditable ? "black" : "grey", // Change text color when editable
                          },
                        }}
                      />
                      <IconButton onClick={handleSubmission_idClick}>
                        <EditIcon />
                      </IconButton>
                    </Box>
                  </>
                )}

                {/* Comment text input */}
                <TextField
                  label="Comment"
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  fullWidth
                  margin="normal"
                  multiline
                  rows={4}
                  required
                  sx={{
                    "& .MuiInputLabel-root": { color: "var(--theme-color)" },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "var(--theme-color)",
                    },
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "var(--theme-color)",
                      },
                      "&:hover fieldset": {
                        borderColor: "var(--theme-color)",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "var(--theme-color)",
                      },
                    },
                  }}
                />

                {/* Submit button */}
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  fullWidth
                  sx={{
                    height: "40px",
                    backgroundColor: "var(--theme-color) !important",
                    color: "var(--theme-btn-clr) !important",
                    fontWeight: "900 !important",
                    textTransform: "capitalize !important",
                    lineHeight: "unset",
                    fontSize: "0.8rem !important",
                  }}
                >
                  Submit Comment
                </Button>
              </form>
            </Paper>
          </AccordionDetails>
        </Accordion>
      </Box>
      <Dialog
        open={openEditDialog}
        onClose={() => setOpenEditDialog(false)}
        PaperProps={{
          sx: {
            backgroundColor: "var(--theme-btn-clr)",
            border: "1px solid var(--theme-color)",
          },
        }}
      >
        <DialogTitle
          sx={{
            color: "var(--theme-color)",
            fontWeight: 700,
            borderBottom: "1px solid var(--theme-color)",
          }}
        >
          Edit Comment
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: "var(--theme-bg-header)" }}>
          {user && !globalRoles.isAdmin && (
            <>
              <input type="hidden" value={editFirstName} />
              <input type="hidden" value={editLastName} />
              <input type="hidden" value={editEmail} />
            </>
          )}

          {/* If the user is an admin, show the fields but auto-populate them */}
          {globalRoles.isAdmin && (
            <>
              <TextField
                label="First Name"
                value={editFirstName}
                onChange={(e) => setEditFirstName(e.target.value)}
                fullWidth
                margin="normal"
                sx={{
                  "& .MuiInputLabel-root": { color: "var(--theme-color)" },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "var(--theme-color)",
                  },
                  "& .MuiOutlinedInput-root": {
                    color: "var(--theme-color)",
                    backgroundColor: "var(--theme-btn-clr)",
                    "& fieldset": {
                      borderColor: "var(--theme-color)",
                    },
                    "&:hover fieldset": {
                      borderColor: "var(--theme-color)",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "var(--theme-color)",
                    },
                  },
                }}
              />
              <TextField
                label="Last Name"
                value={editLastName}
                onChange={(e) => setEditLastName(e.target.value)}
                fullWidth
                margin="normal"
                sx={{
                  "& .MuiInputLabel-root": { color: "var(--theme-color)" },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "var(--theme-color)",
                  },
                  "& .MuiOutlinedInput-root": {
                    color: "var(--theme-color)",
                    backgroundColor: "var(--theme-btn-clr)",
                    "& fieldset": {
                      borderColor: "var(--theme-color)",
                    },
                    "&:hover fieldset": {
                      borderColor: "var(--theme-color)",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "var(--theme-color)",
                    },
                  },
                }}
              />
              <TextField
                label="Email"
                value={editEmail}
                onChange={(e) => setEditEmail(e.target.value)}
                fullWidth
                margin="normal"
                sx={{
                  "& .MuiInputLabel-root": { color: "var(--theme-color)" },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "var(--theme-color)",
                  },
                  "& .MuiOutlinedInput-root": {
                    color: "var(--theme-color)",
                    backgroundColor: "var(--theme-btn-clr)",
                    "& fieldset": {
                      borderColor: "var(--theme-color)",
                    },
                    "&:hover fieldset": {
                      borderColor: "var(--theme-color)",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "var(--theme-color)",
                    },
                  },
                }}
              />
              {/* UID Field - Greyed out by default, editable when clicking the pencil icon */}
              <Box display="flex" alignItems="center">
                <TextField
                  label="UID"
                  value={editUID}
                  onChange={(e) => setEditUID(e.target.value)}
                  fullWidth
                  margin="normal"
                  InputProps={{
                    readOnly: !isUIDEditable, // Greyed out unless toggled editable
                  }}
                  sx={{
                    "& .MuiInputLabel-root": { color: "var(--theme-color)" },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "var(--theme-color)",
                    },
                    "& .MuiOutlinedInput-root": {
                      color: "var(--theme-color)",
                      backgroundColor: "var(--theme-btn-clr)",
                      "& fieldset": {
                        borderColor: "var(--theme-color)",
                      },
                      "&:hover fieldset": {
                        borderColor: "var(--theme-color)",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "var(--theme-color)",
                      },
                    },
                    input: {
                      color: isUIDEditable ? "black" : "grey", // Change text color when editable
                    },
                  }}
                />
                <IconButton
                  onClick={handleUIDClick}
                  sx={{ color: "var(--theme-color)" }}
                >
                  <EditIcon />
                </IconButton>
              </Box>

              {/* Submission_id Field - Greyed out by default, editable when clicking the pencil icon */}
              <Box display="flex" alignItems="center">
                <TextField
                  label="Submission_id"
                  value={editSubmission_id}
                  onChange={(e) => setEditSubmission_id(e.target.value)}
                  fullWidth
                  margin="normal"
                  InputProps={{
                    readOnly: !isSubmission_idEditable, // Greyed out unless toggled editable
                  }}
                  sx={{
                    "& .MuiInputLabel-root": { color: "var(--theme-color)" },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "var(--theme-color)",
                    },
                    "& .MuiOutlinedInput-root": {
                      color: "var(--theme-color)",
                      backgroundColor: "var(--theme-btn-clr)",
                      "& fieldset": {
                        borderColor: "var(--theme-color)",
                      },
                      "&:hover fieldset": {
                        borderColor: "var(--theme-color)",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "var(--theme-color)",
                      },
                    },
                    input: {
                      color: isSubmission_idEditable ? "black" : "grey", // Change text color when editable
                    },
                  }}
                />
                <IconButton
                  onClick={handleSubmission_idClick}
                  sx={{ color: "var(--theme-color)" }}
                >
                  <EditIcon />
                </IconButton>
              </Box>
            </>
          )}

          {/* Comment text input */}
          <TextField
            label="Comment"
            value={editCommentText}
            onChange={(e) => setEditCommentText(e.target.value)}
            fullWidth
            margin="normal"
            multiline
            rows={4}
            required
            sx={{
              "& .MuiInputLabel-root": { color: "var(--theme-color)" },
              "& .MuiInputLabel-root.Mui-focused": {
                color: "var(--theme-color)",
              },
              "& .MuiOutlinedInput-root": {
                color: "var(--theme-color)",
                backgroundColor: "var(--theme-btn-clr)",
                "& fieldset": {
                  borderColor: "var(--theme-color)",
                },
                "&:hover fieldset": {
                  borderColor: "var(--theme-color)",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "var(--theme-color)",
                },
              },
            }}
          />
        </DialogContent>
        <DialogActions
          sx={{
            borderTop: "1px solid var(--theme-color)",
            backgroundColor: "var(--theme-btn-clr)",
          }}
        >
          <Button
            onClick={() => setOpenEditDialog(false)}
            sx={{
              color: "var(--theme-color)",
              textTransform: "capitalize",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleEditSubmit}
            variant="contained"
            sx={{
              backgroundColor: "var(--theme-color) !important",
              color: "var(--theme-btn-clr) !important",
              textTransform: "capitalize",
              fontWeight: 700,
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CommentsSection;
